/**
 * Resolves the best display name for an AD object based on its type.
 *
 * Priority varies by AD object type:
 * - Users: samaccountname > name > displayname > identifier
 * - Computers: name (hostname) > identifier
 * - Groups: name > displayname > identifier
 * - Domains: domain > name > identifier
 */

interface ADAssetData {
  key?: string;
  identifier?: string;
  group?: string;
  class?: string;
  name?: string;
  displayName?: string;
  displayname?: string;
  samaccountname?: string;
  distinguishedname?: string;
  domain?: string;
}

/**
 * Extract AD object type from asset key.
 * Key format: #<type>#<group>#<identifier>
 * Examples: #aduser#wraith.corp#S-1-5-21-..., #adcomputer#wraith.corp#DC01$
 */
export function getADObjectType(key?: string): string | null {
  if (!key) return null;
  const parts = key.split('#').filter(Boolean);
  if (parts.length >= 1 && parts[0].startsWith('ad')) {
    return parts[0]; // 'aduser', 'adcomputer', 'adgroup', 'addomain', etc.
  }
  return null;
}

/**
 * Resolve the best display name for an AD object based on its type.
 */
export function resolveADDisplayName(asset: ADAssetData): string {
  const adType = getADObjectType(asset.key) || asset.class;

  // Normalize field access (handle both camelCase and lowercase)
  const displayname = asset.displayName || asset.displayname;
  const name = asset.name;
  const samaccountname = asset.samaccountname;
  const identifier = asset.identifier;
  const domain = asset.domain || asset.group;

  switch (adType) {
    case 'aduser':
      // Users: SAM account name is the canonical identifier
      return (
        samaccountname || name || displayname || identifier || 'Unknown User'
      );

    case 'adcomputer':
      // Computers: Use name (hostname), strip domain suffix if present
      if (name) {
        return name.split('.')[0]; // "DC01.wraith.corp" -> "DC01"
      }
      return identifier || 'Unknown Computer';

    case 'adgroup':
      // Groups: Name is most descriptive
      return name || displayname || identifier || 'Unknown Group';

    case 'addomain':
      // Domains: Domain name is most meaningful
      return domain || name || identifier || 'Unknown Domain';

    default:
      // Generic AD object or non-AD asset
      return displayname || name || samaccountname || identifier || '';
  }
}

/**
 * Check if an asset is an AD object based on its key.
 */
export function isADAsset(key?: string): boolean {
  if (!key) return false;
  const parts = key.split('#').filter(Boolean);
  return parts.length >= 1 && parts[0].startsWith('ad');
}
